import type { UnknownAction } from '@reduxjs/toolkit'

import {
  defineListener,
  errorView,
  resourceListTag,
  connectionRestored,
  isRetryableError,
  resourceScopeCleared,
  resourceTag,
  userScopeCleared,
  CANCEL_QUEUED_TEXT,
  trace,
  type ModuleListener,
  type ModuleListenerApi,
} from '@krp/client-runtime'

import { sameId } from '../../../shared/api/wire'
import { AQSI_TAGS } from '../../../shared/api/tags'
import { storeDispatch, type AqsiDispatch } from '../../../shared/model/dispatch'
import { feedbackOfError, refusedBeforeEffect, type CommandFeedback } from '../../../shared/model/feedback'
import { FEEDBACK_SCOPES, feedbackSet } from '../../../shared/model/feedbackSlice'
import { newCommandIntent, newUuid } from '../../../shared/model/ids'
import { belongsToResource, ridKey } from '../../../shared/model/keys'
import { aqsiCommonDeliver, aqsiCommonDeliveryJob, aqsiCommonGoods, type CommonGoodsArgs } from '../api/commonSearchApi'
import { aqsiMySession, aqsiPrintSettings } from '../../rmk/api/rmkApi'
import { probeRegister } from '../../rmk/model/probe'
import type { AqsiGood } from '../../catalog/model/types'
import { absentReason, DELIVERED_STATES, type CommonDeliverResult, type CommonGoodHit, type DeliveryOffer } from './commonSearch'
import {
  aqsiActiveSale,
  aqsiHeldCartDrop,
  aqsiHeldCartRestore,
  aqsiOperationResolve,
  aqsiParkedSales,
  aqsiSale,
  aqsiSaleCancel,
  aqsiSaleCancelPayment,
  aqsiSaleFiscalize,
  aqsiSaleHold,
  aqsiSaleIssuedOutside,
  aqsiSaleLatePayment,
  aqsiSaleOperation,
  aqsiSaleReceiptRetry,
  aqsiSalePark,
  aqsiSaleUnpark,
  aqsiSalePay,
  aqsiSaleReconcile,
  saleApi,
  type SaleCommandArgs,
} from '../api/saleApi'
import {
  cartDiscardRequested,
  cartFlushRequested,
  cartRetryRequested,
  heldCartDropRequested,
  heldCartRestoreRequested,
  lookupCancelRequested,
  operationResolveCheckRequested,
  operationResolveRequested,
  saleBarcodeScanned,
  saleCancelRequested,
  salePaymentCancelRequested,
  saleFiscalizeRequested,
  saleReceiptRetryRequested,
  saleParkRequested,
  saleLatePaymentRequested,
  saleUnparkRequested,
  saleHoldRequested,
  saleIssuedOutsideRequested,
  salePayRequested,
  salePayWithRequested,
  salePreparationCancelRequested,
  saleDeliveryRequested,
  salePaymentKindRequested,
  saleReconcileRequested,
  saleReloadRequested,
  saleSearchSubmitted,
  saleStatusCheckRequested,
} from './commands'
import { CART_DEBOUNCE_MS, CART_PRICE_DEBOUNCE_MS, EXACT_BARCODE_PAGE_SIZE, LOOKUP_PAGE_SIZE, NOT_ARRIVED_AFTER_MS, retryDelayMs } from '@krp/sale-ui'
import { priceError, priceInputText } from '@krp/sale-ui'
import { quantityError } from '@krp/sale-ui'
import { classifyCartFailure, failureOf, sendCartRequest } from './cartQueue'
import { canEditCart } from './cartRules'
import { canHandOffToBackground, receiptsAheadOf, type ReceiptsAhead } from './fastPayment'
import {
  fastReceiptDismissed,
  fastReceiptDone,
  fastReceiptHandedOff,
  isFastReceiptPending,
  type FastReceiptsState,
} from './fastReceiptsSlice'
import { FAST_HAND_OFF_REASON, PAY_WAITING_RECEIPT_TEXT } from './fastTexts'
import { commandAttemptText, commandRetryText, failureWithoutAnswer, PAY_CHECKING_LINK_TEXT, PAY_SAVING_CART_TEXT } from './healthTexts'
import {
  cartAttemptFailed,
  cartAttemptStarted,
  cartDraftDropped,
  cartDraftOf,
  cartDraftsRestored,
  cartRejected,
  cartRestored,
  cartSaved,
  cartSendStarted,
  cartSendVersioned,
} from './cartSlice'
import {
  blocksSaleCommands,
  saleCommandAttempt,
  saleCommandNotArrived,
  saleCommandOf,
  saleCommandSettled,
  saleCommandStarted,
  saleCommandUnknown,
  saleCommandWaiting,
} from './saleCommandsSlice'
import {
  cartEdited,
  deliveryDismissed,
  deliveryOffered,
  deliveryPhaseChanged,
  lineSelected,
  lookupAttempt,
  lookupDone,
  lookupQueued,
  lookupWaiting,
  outsideClosed,
  retryClosed,
  parkClosed,
  paymentKindChosen,
  resolveDraftOf,
  searchTextChanged,
  resolvePhaseChanged,
  saleFinishedShown,
  saleSeatCleared,
  seatIntentOf,
  seatKey,
  type SeatRef,
  type SeatWithDevice,
} from './saleSlice'
import { saleOf, seatRefundOf } from './seatActivity'
import {
  paymentAttemptLive,
  paymentAttemptOf,
  paymentCancelPressed,
  paymentCancelQueued,
  paymentClosed,
  paymentFeedbackSet,
  paymentResolveClosed,
  SAVE_COMMAND,
  WAIT_RECEIPT_COMMAND,
  type PaymentAttemptsState,
} from './paymentAttemptsSlice'
import type {
  AqsiSale,
  AqsiSaleOperation,
  CartBase,
  CartQueueState,
  PaymentKind,
  PendingLookup,
  SaleCommand,
  SaleCommandKind,
  SaleCommandsState,
  SaleIntentState,
} from './types'

export interface SaleModuleSlices {
  sale: SaleIntentState
  /** Очередь корзины: несохранённая правка и замороженный запрос отправки (RP-SALE-FAST). */
  cart: CartQueueState
  commands: SaleCommandsState
  /** Попытка оплаты с окном (RP-PAY-ONE); модуль без неё — итоги команд цикла пишутся только в окно, которого нет. */
  attempts?: PaymentAttemptsState
  /** Продажи, чек которых печатается в фоне (быстрая оплата, RP-SALE-FAST шаг 9). */
  fast?: FastReceiptsState
}

/**
 * Итог команды цикла оплаты — в окно попытки (RP-PAY-ONE, шаг 5), а не в общий итог кассы: внизу уведомлений оплаты нет, и общий итог
 * кассы оплатой не перетирается.
 */
function sayInPaymentWindow(dispatch: AqsiDispatch, ref: SeatRef, saleId: string | null, feedback: CommandFeedback, kind?: PaymentKind): void {
  dispatch(paymentFeedbackSet({ resourceId: ref.resourceId, registerKey: ref.registerKey, saleId, feedback, ...(kind !== undefined ? { kind } : {}) }))
}

const saleFeedbackKey = (ref: SeatRef) => ridKey(ref.resourceId, FEEDBACK_SCOPES.sale(ref.registerKey))
const resolveFeedbackKey = (resourceId: string, operationId: string) =>
  ridKey(resourceId, FEEDBACK_SCOPES.resolve(operationId))

function note(command: string, target: string, phase: CommandFeedback['phase'], text: string | null): CommandFeedback {
  return { command, target, phase, error: null, note: text }
}

/** Товар можно продать — как решит служба при сохранении корзины (C3b), словами — в подборе. */
function sellable(good: AqsiGood): boolean {
  return good.invalidReason === null && good.missingSince === null && good.price !== null
}

/** Итог запроса товара: найден / не найден / запрос не получил ответа (`failure` — отказ транспорта как есть). */
interface FoundGood {
  good: AqsiGood | null
  ambiguous: boolean
  barcode: string | null
  failure?: unknown
  /** Товар есть в общем каталоге, но карточки в кассе нет: РМК не молчит, а говорит причину и предлагает отправить его в кассу. */
  absent?: { productId: string; name: string; reason: string }
}

/** Штрихкод целиком по форме: EAN/UPC/ITF — только цифры. Другая строка (наименование, артикул с буквами) идёт в текстовый поиск. */
const WHOLE_BARCODE = /^\d+$/

/** Пауза между проверками, появилась ли карточка отправленного товара, и предел ожидания. */
const DELIVERY_POLL_MS = 3_000
const DELIVERY_WAIT_MS = 180_000
/** Касса отказала, а слов не дала. */
const DELIVERY_REFUSED_TEXT = 'касса не приняла товар и не назвала причину'
const DELIVERY_TIMEOUT_TEXT = 'товар отправлен в кассу, но его карточка там ещё не появилась — нажмите кнопку позже'

function foundOf(hit: CommonGoodHit): FoundGood {
  if (hit.good !== null) return { good: hit.good, ambiguous: false, barcode: hit.matchedBarcode }
  return {
    good: null,
    ambiguous: false,
    barcode: hit.matchedBarcode,
    absent: { productId: hit.productId, name: hit.name, reason: absentReason(hit) },
  }
}

/** Свежий ответ общего поиска: скан нового товара не должен получить кэш прежней минуты. */
function commonGoods(dispatch: AqsiDispatch, args: CommonGoodsArgs) {
  return dispatch(aqsiCommonGoods.initiate(args, { subscribe: false, forceRefetch: true }))
}

/**
 * Товар по строке поиска РМК. Ищет общий каталог — тем же поиском, что окно «Номенклатура» (Platform: штрихкод и артикул целиком точно из базы,
 * текст — единая служба text-search); карточка кассы берётся по ключу из карты идентичности. Скан и Enter по строке из цифр сначала спрашивают
 * ТОЧНО по штрихкоду. Скан — это только штрихкод: пустой точный ответ значит «не найден». Enter по цифрам может быть и артикулом: при пустом
 * точном ответе идёт текстовый поиск, но в чек попадает только карточка с ТЕМ ЖЕ штрихкодом или артикулом целиком — «похожий» товар по коду
 * из цифр не добавляется никогда (К-115: склеенный или недочитанный код иначе клал в чек соседний товар). Наименование (не одни цифры) — первый
 * продаваемый или ещё не заведённый в кассу. Товар без карточки в кассе возвращается как `absent`. Сбой запроса — НЕ «ничего не найдено»: он
 * возвращается как `failure`.
 */
async function findGood(dispatch: AqsiDispatch, resourceId: string, text: string, command: PendingLookup['command']): Promise<FoundGood> {
  const scan = command === 'sale.scan'
  const digits = WHOLE_BARCODE.test(text)
  if (scan || digits) {
    const exactResult = await commonGoods(dispatch, { resourceId, text, mode: 'barcode', limit: EXACT_BARCODE_PAGE_SIZE })
    if (exactResult.error !== undefined) return { good: null, ambiguous: false, barcode: null, failure: exactResult.error }
    const matched = exactResult.data?.hits ?? []
    if (matched.length > 1) return { good: null, ambiguous: true, barcode: text }
    const only = matched[0]
    if (only !== undefined) return foundOf(only)
    // Камера читает штрихкод: нет товара с этим кодом — значит, не найден; «похожий» товар в чек не попадает.
    if (scan) return { good: null, ambiguous: false, barcode: null }
  }
  const result = await commonGoods(dispatch, { resourceId, text, mode: 'text', limit: LOOKUP_PAGE_SIZE })
  if (result.error !== undefined) return { good: null, ambiguous: false, barcode: null, failure: result.error }
  const hits = result.data?.hits ?? []
  const exact = hits.filter((hit) => hit.matchedBarcode === text)
  if (exact.length > 1) return { good: null, ambiguous: true, barcode: text }
  const byCode = exact[0]
  if (byCode !== undefined) return foundOf(byCode)
  if (digits) {
    // Код из цифр — штрихкод или артикул, а не наименование: только точное совпадение артикула, иначе «не найдено».
    const bySku = hits.filter((hit) => hit.sku === text)
    if (bySku.length > 1) return { good: null, ambiguous: true, barcode: text }
    const sku = bySku[0]
    return sku === undefined ? { good: null, ambiguous: false, barcode: null } : foundOf(sku)
  }
  // Порядок выдачи — единого поиска; непродаваемые карточки пропускаются, а товар без карточки в кассе предлагается к отправке.
  const pick = hits.find((hit) => hit.good === null || sellable(hit.good)) ?? hits[0]
  return pick === undefined ? { good: null, ambiguous: false, barcode: null } : foundOf(pick)
}

/**
 * Ответ команды — в кэш RTK Query, а не в slice. Завершённая продажа больше
 * не «активная» у кассы (служба держит одну незавершённую на кассу), поэтому
 * активной становится `null`, а сама продажа остаётся для итога.
 */
function showSale(dispatch: AqsiDispatch, ref: SeatWithDevice, sale: AqsiSale): void {
  // Запоздавший ответ (итог отмены после нового чтения, ответ после живого события) не откатывает продажу назад: та же продажа новее — остаётся.
  const selectActive = aqsiActiveSale.select(ref)
  const shown = saleOf(dispatch((_, getState) => selectActive(getState() as Parameters<typeof selectActive>[0]).data))
  if (shown !== null && sameId(shown.id, sale.id) && shown.version > sale.version) return
  // Отложенная продажа кассу не держит: активной становится `null`, а продажа — в списке отложенных (RP-FB35 K9).
  if (sale.isParked) {
    void dispatch(saleApi.util.upsertQueryData('aqsiActiveSale', ref, null))
    return
  }
  if (!sale.isFinished) {
    void dispatch(saleApi.util.upsertQueryData('aqsiActiveSale', ref, sale))
    return
  }
  void dispatch(saleApi.util.upsertQueryData('aqsiSale', { resourceId: ref.resourceId, saleId: sale.id }, sale))
  void dispatch(saleApi.util.upsertQueryData('aqsiActiveSale', ref, null))
  if (sale.fiscal === 'Succeeded' || sale.fiscal === 'IssuedOutside') dispatch(saleFinishedShown({ ...ref, saleId: sale.id }))
}

function rereadSeat(dispatch: AqsiDispatch, ref: SeatRef): void {
  dispatch(saleApi.util.invalidateTags([resourceTag(AQSI_TAGS.saleActive, ref.resourceId, ref.registerKey)]))
}

/** Продажа уже знает нашу команду: служба записала её операцию (наш UUID) или завершила продажу. */
function saleKnowsCommand(sale: AqsiSale, command: SaleCommand): boolean {
  const id = command.operationId
  switch (command.kind) {
    case 'pay':
      return (
        (sale.purchaseOperationId !== null && sameId(sale.purchaseOperationId, id)) ||
        (sale.receiptOperationId !== null && sameId(sale.receiptOperationId, id))
      )
    case 'fiscalize':
      return sale.receiptOperationId !== null && sameId(sale.receiptOperationId, id)
    case 'cancel':
    case 'outside':
      return sale.isFinished
  }
}

const COMMAND_TEXT: Readonly<Record<SaleCommandKind, { sending: string; done: string; unknown: string }>> = {
  pay: {
    sending: 'Оплата отправляется…',
    done: 'Оплата отправлена — ход виден ниже',
    unknown:
      'Ответ на оплату не получен: неизвестно, ушло ли списание или чек. Повторная оплата запрещена — продажа перечитана, смотрите её состояние.',
  },
  fiscalize: {
    sending: 'Чек отправляется…',
    done: 'Чек отправлен — ход виден ниже',
    unknown: 'Ответ на чек не получен: неизвестно, пробит ли он. Повтор запрещён — продажа перечитана.',
  },
  cancel: {
    sending: 'Продажа отменяется…',
    done: 'Продажа отменена',
    unknown: 'Ответ на отмену не получен. Продажа перечитана — проверьте, отменена ли она.',
  },
  outside: {
    sending: 'Служба ищет чек в журнале кассы…',
    done: 'Чек пробит на кассе вне программы',
    unknown: 'Ответ не получен: неизвестно, закрыта ли продажа этим чеком. Повтор запрещён — продажа перечитана.',
  },
}

/** Что дополнительно уходит в команде: у «чек уже пробит» — причина и номер чека. */
type CommandExtra = { reason: string; receiptId: string | null } | null

function sendSaleCommand(dispatch: AqsiDispatch, kind: SaleCommandKind, args: SaleCommandArgs, extra: CommandExtra) {
  switch (kind) {
    case 'pay':
      return dispatch(aqsiSalePay.initiate(args, { track: false })).unwrap()
    case 'fiscalize':
      return dispatch(aqsiSaleFiscalize.initiate(args, { track: false })).unwrap()
    case 'cancel':
      return dispatch(aqsiSaleCancel.initiate(args, { track: false })).unwrap()
    case 'outside':
      return dispatch(
        aqsiSaleIssuedOutside.initiate(
          { ...args, reason: extra?.reason ?? '', receiptId: extra?.receiptId ?? null },
          { track: false },
        ),
      ).unwrap()
  }
}

/** Команды, которые повторяются тем же ключом при неизвестном исходе: оплата и чек. */
const REPEATED_COMMANDS: ReadonlySet<SaleCommandKind> = new Set(['pay', 'fiscalize'])

/** Отказы «отложить», которые проходят сами: чек ещё готовится к отправке, версия продажи ушла вперёд (сбой связи — тоже, см. `isRetryableError`). */
const HAND_OFF_RETRYABLE: ReadonlySet<string> = new Set(['receipt-sending', 'version-mismatch'])

/** Столько раз подряд уход в фон может не удаться, прежде чем окно оплаты поведёт продажу до конца само. */
const HAND_OFF_MAX_FAILURES = 5

/** Подготовка оплаты «одним нажатием»: отмена кассира и ожидание очереди корзины. */
interface PayPreparation {
  cancelled: boolean
  /** Ждёт ли подготовка очередь корзины и как проверить, что очередь закончила. */
  settled: (() => boolean) | null
  resolve: (() => void) | null
}

/** Основа черновика корзины — продажа такой, какой её знает сервер (позиции с ценой кассира). */
function cartBaseOf(sale: AqsiSale | null): CartBase {
  return {
    saleId: sale?.id ?? null,
    version: sale?.version ?? 0,
    contact: sale?.customerContact ?? null,
    lines: (sale?.lines ?? []).map((line) => ({
      productKey: line.productKey,
      name: line.name,
      barcode: line.barcode,
      quantity: line.quantity,
      isWeight: line.isWeight,
      unit: line.unit,
      price: line.catalogPriceKopecks != null ? priceInputText(line.priceKopecks) : null,
    })),
  }
}

/**
 * Listeners окна продажи. `select` — где в состоянии модуля лежат намерение
 * продажи и ждущие команды (модуль собирает свои слайсы сам).
 */
export function createSaleListeners<S>(select: (state: S) => SaleModuleSlices): ModuleListener<S>[] {
  /** Таймеры debounce корзины по месту: `<rid>|<registerKey>`. */
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  /** Очередь корзины работает на кассе (одна отправка за раз): повторный запуск ничего не делает, очередь сама заберёт новые правки. */
  const cartFlights = new Set<string>()
  /**
   * Команды оплаты, ждущие паузу перед повтором тем же ключом: `pay|<касса>`, `fiscalize|<касса>`, `cancel-payment|<касса>`. Будят «связь
   * вернулась» (повторить сразу) и «Отменить» (остановить повтор).
   */
  const retryWaits = new Map<string, (stop: boolean) => void>()
  /** «Отменить» нажато у оплаты, чей запрос ещё в пути или ждёт повтора: после этой попытки повтора не будет. */
  const commandStops = new Set<string>()
  /** Очередь поисков товара работает на кассе (RP-SALE-FAST, шаг 7а): один обработчик за раз, новые коды встают в конец. */
  const lookupFlights = new Set<string>()
  /** «Отправить в кассу сейчас» идёт по кассе: второе нажатие не шлёт вторую раздачу. */
  const deliveryFlights = new Set<string>()
  /** Подготовка оплаты по кассе (до команды оплаты): ждёт очередь корзины; «Отменить» гасит её. */
  const payPreparations = new Map<string, PayPreparation>()
  /** Пауза очереди перед повтором: «Повторить сейчас», «Вернуть как на сервере» и сброс области будят её, не дожидаясь таймера. */
  const cartWaits = new Map<string, () => void>()
  /** Сверки в полёте: опрос хода не накладывает вторую на ту же операцию. */
  const reconciling = new Set<string>()
  /** Смена способа оплаты в полёте по месту: второй щелчок не шлёт вторую отмену. */
  const switching = new Set<string>()
  /** «Чека нет — пробить заново» в полёте: двойное нажатие — одна команда (в памяти, не в sessionStorage: служба идемпотентна по версии). */
  const retryingReceipts = new Set<string>()
  /** «Отложить» / «Вернуть на кассу» в полёте: двойное нажатие — одна команда. */
  const parkFlights = new Set<string>()
  /**
   * Быстрая оплата: продажи, которые в фон уйти не смогли (служба отказала) — окно ведёт их до конца, как без быстрой оплаты. Ключ —
   * `<rid>|<saleId>`; только память вкладки.
   */
  const handOffRefused = new Set<string>()
  /** Сколько раз подряд уход в фон не удался по причине, которая может пройти сама (версия, «чек ещё готовится»). */
  const handOffFailures = new Map<string, number>()
  /** Решение по запоздавшему слипу в полёте: двойное нажатие — одна команда. */
  const latePaymentFlights = new Set<string>()
  /** Отмена оплаты на терминале в полёте: второй щелчок не шлёт вторую просьбу. */
  const cancellingPayments = new Set<string>()
  /**
   * Эпоха области: выход пользователя и сброс rid её сдвигают. Ответ, пришедший
   * после сброса, не пишет в кэш и намерение ушедшей области (G4).
   */
  let userEpoch = 0
  const resourceEpochs = new Map<string, number>()
  const epochOf = (resourceId: string) => `${userEpoch}:${resourceEpochs.get(resourceId.toLowerCase()) ?? 0}`

  const dropTimers = (match: (key: string) => boolean) => {
    for (const [key, timer] of timers) {
      if (!match(key)) continue
      clearTimeout(timer)
      timers.delete(key)
    }
    for (const [key, wake] of [...cartWaits]) if (match(key)) wake()
    for (const [key, wake] of [...retryWaits]) if (match(key.slice(key.indexOf('|') + 1))) wake(true)
  }

  /** Подождать `ms` перед повтором команды оплаты; `true` — остановить повтор («Отменить», сброс области), `false` — срок вышел или связь вернулась. */
  function waitForCommandRetry(key: string, ms: number): Promise<boolean> {
    return new Promise((resolve) => {
      const wake = (stop: boolean) => {
        clearTimeout(timer)
        retryWaits.delete(key)
        resolve(stop)
      }
      const timer = setTimeout(() => wake(false), ms)
      retryWaits.set(key, wake)
    })
  }

  /** Подготовка оплаты ждёт, пока очередь корзины не опустеет (или не откажет): без таймеров — по событиям очереди. */
  function untilCartSettled(api: ModuleListenerApi<S>, ref: SeatRef, prep: PayPreparation): Promise<void> {
    const settled = () => {
      const draft = cartDraftOf(select(api.getModuleState()).cart, ref)
      return draft === undefined || draft.phase === 'rejected'
    }
    if (prep.cancelled || settled()) return Promise.resolve()
    return new Promise((resolve) => {
      prep.settled = settled
      prep.resolve = () => {
        prep.settled = null
        prep.resolve = null
        resolve()
      }
    })
  }

  /** Быстрая оплата включена у кассы: настройка читается из кэша (экран подписан на неё) или один раз с сервера; не прочитана — выключена. */
  async function fastPaymentOn(dispatch: AqsiDispatch, ref: SeatWithDevice): Promise<boolean> {
    try {
      const settings = await dispatch(aqsiPrintSettings.initiate({ resourceId: ref.resourceId, deviceId: ref.deviceId }, { subscribe: false })).unwrap()
      return settings.fastPayment
    } catch {
      return false
    }
  }

  /** Фоновые чеки кассы по списку отложенных продаж из кэша; список ещё не читали — `null`. */
  function cachedReceiptsAhead(dispatch: AqsiDispatch, ref: SeatWithDevice): ReceiptsAhead | null {
    const selectParked = aqsiParkedSales.select({ resourceId: ref.resourceId, deviceId: ref.deviceId })
    const rows = dispatch((_, getState) => selectParked(getState() as Parameters<typeof selectParked>[0]).data)
    return rows === undefined ? null : receiptsAheadOf(rows, Date.now())
  }

  /** Оплата ждёт, пока чек предыдущей продажи допечатается: без таймеров — по свежему чтению списка отложенных продаж. */
  function untilReceiptsPrinted(dispatch: AqsiDispatch, ref: SeatWithDevice, prep: PayPreparation): Promise<void> {
    const settled = () => {
      const ahead = cachedReceiptsAhead(dispatch, ref)
      return ahead !== null && ahead.printing === 0
    }
    if (prep.cancelled || settled()) return Promise.resolve()
    return new Promise((resolve) => {
      prep.settled = settled
      prep.resolve = () => {
        prep.settled = null
        prep.resolve = null
        resolve()
      }
    })
  }

  /**
   * Быстрая оплата (RP-SALE-FAST, шаг 9): деньги взяты, чек у кассы — продажа откладывается на сервере, окно оплаты закрывается, кассир
   * набирает следующую. Службе отказали — окно ведёт продажу до конца, как без быстрой оплаты.
   */
  async function handOffToBackground(api: ModuleListenerApi<S>, ref: SeatWithDevice, sale: AqsiSale): Promise<void> {
    if (!canHandOffToBackground(sale)) return
    const dispatch = storeDispatch(api.dispatch)
    const saleKey = ridKey(ref.resourceId, sale.id.toLowerCase())
    const flight = ridKey(ref.resourceId, `handoff:${sale.id.toLowerCase()}`)
    if (handOffRefused.has(saleKey) || parkFlights.has(flight)) return
    // Команда по продаже в пути или с неизвестным исходом, ручная сверка: окно ведёт её само.
    if (blocksSaleCommands(saleCommandOf(select(api.getModuleState()).commands, ref))) return
    parkFlights.add(flight)
    const epoch = epochOf(ref.resourceId)
    try {
      if (!(await fastPaymentOn(dispatch, ref)) || epochOf(ref.resourceId) !== epoch) return
      try {
        const parked = await dispatch(
          aqsiSalePark.initiate({ ...ref, saleId: sale.id, intent: newCommandIntent(), expectedVersion: sale.version, reason: FAST_HAND_OFF_REASON }, { track: false }),
        ).unwrap()
        if (epochOf(ref.resourceId) !== epoch) return
        handOffFailures.delete(saleKey)
        // Сумма для галочки «Оплата прошла» берётся из кэша продажи; галочка ставится ДО ухода продажи с кассы, чтобы окно не мигало.
        void dispatch(saleApi.util.upsertQueryData('aqsiSale', { resourceId: ref.resourceId, saleId: parked.id }, parked))
        dispatch(fastReceiptHandedOff({ ...ref, saleId: parked.id }))
        showSale(dispatch, ref, parked)
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        const code = errorView(error).code
        const failures = (handOffFailures.get(saleKey) ?? 0) + 1
        handOffFailures.set(saleKey, failures)
        // «Чек ещё готовится к отправке» и версия, ушедшая вперёд, проходят сами: следующее чтение продажи повторит. Остальное и
        // повторяющиеся неудачи — окно ведёт продажу до конца.
        if (!(HAND_OFF_RETRYABLE.has(code) || isRetryableError(error)) || failures >= HAND_OFF_MAX_FAILURES) handOffRefused.add(saleKey)
        rereadSeat(dispatch, ref)
      }
    } finally {
      parkFlights.delete(flight)
    }
  }

  /** Подождать `ms` перед повтором отправки корзины; `cartWaits` будит раньше срока. */
  function waitForCartRetry(key: string, ms: number): Promise<void> {
    return new Promise((resolve) => {
      const wake = () => {
        clearTimeout(timer)
        cartWaits.delete(key)
        resolve()
      }
      const timer = setTimeout(wake, ms)
      cartWaits.set(key, wake)
    })
  }

  async function runSaleCommand(
    kind: SaleCommandKind,
    payload: SeatWithDevice & { saleId: string; expectedVersion: number },
    api: ModuleListenerApi<S>,
    extra: CommandExtra = null,
  ): Promise<void> {
    const dispatch = storeDispatch(api.dispatch)
    const ref = { resourceId: payload.resourceId, registerKey: payload.registerKey, deviceId: payload.deviceId }
    const key = saleFeedbackKey(ref)
    const command = `sale.${kind}`
    const state = select(api.getModuleState())
    // Оплата, чек и «чек уже пробит» — команды цикла оплаты: их ход и отказ — в окне оплаты; «Очистить» — в общем итоге кассы.
    const say = (feedback: CommandFeedback): void => {
      if (kind === 'cancel') dispatch(feedbackSet({ key, feedback }))
      else sayInPaymentWindow(dispatch, ref, payload.saleId, feedback)
    }
    // Неизвестный исход прошлой команды (или двойной клик) запрещает новую: сначала статус (ADR-11).
    const pending = saleCommandOf(state.commands, ref)
    if (blocksSaleCommands(pending)) {
      // Второй щелчок по уходящей команде: баннер «отправляется» уже на экране, отказ его не перекрывает.
      if (pending?.phase === 'sending') return
      say(
        note(
            command,
            ref.registerKey,
            'rejected',
            'Прошлая команда по этой продаже ещё не завершена или её исход неизвестен. Проверьте статус — повторная команда запрещена.',
          ),
      )
      return
    }
    if (cartDraftOf(state.cart, ref) !== undefined) {
      say(
        note(command, ref.registerKey, 'rejected', 'Корзина ещё сохраняется — дождитесь итога сервера.'),
      )
      return
    }
    const intent = newCommandIntent()
    const commandRef = { ...ref, operationId: intent.operationId }
    const seat = seatKey(ref)
    commandStops.delete(seat)
    dispatch(
      saleCommandStarted({
        ...ref,
        kind,
        saleId: payload.saleId,
        saleVersion: payload.expectedVersion,
        operationId: intent.operationId,
        startedAt: new Date().toISOString(),
        phase: 'sending',
        attempt: 0,
        retryAt: null,
        activityAt: new Date().toISOString(),
        lastFailure: null,
      }),
    )
    say(note(command, ref.registerKey, 'sending', COMMAND_TEXT[kind].sending))
    const args: SaleCommandArgs = { ...ref, saleId: payload.saleId, intent, expectedVersion: payload.expectedVersion }
    const epoch = epochOf(ref.resourceId)
    // Оплата и чек повторяются ТЕМ ЖЕ запросом, пока служба не ответит: она узнаёт ключ и отдаёт прежний результат, второго списания и
    // второго чека не будет (RP-SALE-FAST, шаг 6, К-109). Остальные команды — один запрос, исход читается продажей.
    const repeated = REPEATED_COMMANDS.has(kind)
    try {
      for (let attempt = 1; ; attempt += 1) {
        dispatch(saleCommandAttempt({ ...commandRef, attempt, at: new Date().toISOString() }))
        const sentAt = Date.now()
        if (kind === 'pay') trace.mark(attempt > 1 ? 'retry' : 'pay-request', { scope: ref.resourceId, saleId: payload.saleId, attemptKey: intent.operationId, meta: { attempt } })
        if (attempt > 1) {
          const current = saleCommandOf(select(api.getModuleState()).commands, ref)
          if (current !== undefined) say(note(command, ref.registerKey, 'sending', commandAttemptText(current)))
        }
        try {
          const sale = await sendSaleCommand(dispatch, kind, args, extra)
          if (kind === 'pay') trace.mark('pay-response', { saleId: payload.saleId, durationMs: Date.now() - sentAt, outcome: 'ok', meta: { attempt } })
          if (epochOf(ref.resourceId) !== epoch) return
          commandStops.delete(seat)
          dispatch(saleCommandSettled(commandRef))
          if (kind === 'outside') dispatch(outsideClosed(ref))
          showSale(dispatch, ref, sale)
          const done =
            kind === 'outside' && sale.receiptId !== null ? `${COMMAND_TEXT[kind].done}: ${sale.receiptId}` : COMMAND_TEXT[kind].done
          say(note(command, ref.registerKey, 'done', done))
          return
        } catch (error) {
          if (epochOf(ref.resourceId) !== epoch) return
          const view = errorView(error)
          if (kind === 'pay') trace.mark('pay-response', { saleId: payload.saleId, durationMs: Date.now() - sentAt, outcome: view.code, meta: { attempt } })
          if (repeated && isRetryableError(error) && !commandStops.has(seat)) {
            const delay = retryDelayMs(attempt)
            const answered = !failureWithoutAnswer(failureOf(view))
            dispatch(
              saleCommandWaiting({
                ...commandRef,
                failure: failureOf(view),
                retryAt: new Date(Date.now() + delay).toISOString(),
                answeredAt: answered ? new Date().toISOString() : null,
              }),
            )
            const waiting = saleCommandOf(select(api.getModuleState()).commands, ref)
            if (waiting !== undefined) say(note(command, ref.registerKey, 'sending', commandRetryText(waiting)))
            const stop = await waitForCommandRetry(`${kind}|${seat}`, delay)
            if (epochOf(ref.resourceId) !== epoch) return
            if (!stop && !commandStops.has(seat)) continue
          }
          // Конец повторов: служба отказала по существу, кассир остановил повтор или сброс области. Остановленная команда без ответа —
          // «исход неизвестен» (читается продажей); со словами «не выполнено» — отказ без замка.
          commandStops.delete(seat)
          const result = feedbackOfError(command, ref.registerKey, error)
          if (result.phase === 'rejected' && (result.error?.code === 'shift-closed' || result.error?.code === 'shift-not-adopted')) {
            // Отказ оплаты уже установил расхождение с терминалом: не ждём очередного
            // цикла опроса, чтобы вернуть кассира к смене.
            dispatch(saleApi.util.invalidateTags([resourceTag(AQSI_TAGS.shift, ref.resourceId, String(ref.deviceId))]))
          }
          if (result.phase === 'unknown') {
            // Не повторять: продажу читает перечитывание, исход скажет чтение.
            rereadSeat(dispatch, ref)
            dispatch(saleCommandUnknown(commandRef))
            say({ ...result, note: COMMAND_TEXT[kind].unknown })
          } else {
            if (refusedBeforeEffect(error)) rereadSeat(dispatch, ref)
            dispatch(saleCommandSettled(commandRef))
            say(result)
          }
          return
        }
      }
    } finally {
      retryWaits.delete(`${kind}|${seat}`)
    }
  }

  /**
   * Очередь корзины кассы (RP-SALE-FAST, шаг 3): берёт черновик, замораживает запрос (ключ идемпотентности, версия, тело) и шлёт его,
   * пока сервер не ответит. Неизвестный исход (потерянный ответ, таймаут, 5xx, в том числе «не выполнено» с кодом) — не повод выбросить
   * правку: тот же запрос уходит снова с паузами 0,5 → 1 → 2 → 4 → 8 → 10 с, служба узнаёт ключ и отдаёт прежний результат. Конец
   * повтора — подтверждение, отказ по существу (422, 403), 409 (корзину изменили в другом месте — показывается серверная) или «Вернуть как
   * на сервере». Новые правки, пришедшие во время отправки, ждут в черновике и уходят следующей отправкой с новым ключом.
   */
  async function runCartQueue(api: ModuleListenerApi<S>, ref: SeatWithDevice): Promise<void> {
    const dispatch = storeDispatch(api.dispatch)
    const key = saleFeedbackKey(ref)
    const waitKey = seatKey(ref)
    const epoch = epochOf(ref.resourceId)
    const draftNow = () => cartDraftOf(select(api.getModuleState()).cart, ref)
    // Создание продажи после отказа по версии сессии читает её заново (один раз на отправку).
    let forceSession = false
    let versionRetried = false
    for (;;) {
      if (epochOf(ref.resourceId) !== epoch) return
      const draft = draftNow()
      if (draft === undefined) return
      const send = draft.send
      if (send === null) {
        if (draft.phase !== 'dirty') return
        // На кассе места идёт возврат (C4b): служба держит одно незавершённое на кассу и ответит 409 про «продажу» —
        // черновик сбрасывается здесь, без POST/PUT и с понятной причиной.
        const active = await dispatch(aqsiActiveSale.initiate(ref, { subscribe: false }))
        if (epochOf(ref.resourceId) !== epoch) return
        if (seatRefundOf(active.data) !== null) {
          dispatch(cartDraftDropped(ref))
          dispatch(
            feedbackSet({
              key,
              feedback: note('sale.cart', ref.registerKey, 'rejected', 'Правка корзины не применена: на кассе идёт возврат — продажу можно начать после его завершения.'),
            }),
          )
          return
        }
        // Чтение кассы было асинхронным: черновик читается заново.
        const fresh = draftNow()
        if (fresh === undefined || fresh.send !== null || fresh.phase !== 'dirty') continue
        if (blocksSaleCommands(saleCommandOf(select(api.getModuleState()).commands, ref))) {
          // Идёт оплата/чек: корзину служба не примет, а черновик повис бы без отправки.
          dispatch(cartDraftDropped(ref))
          dispatch(
            feedbackSet({
              key,
              feedback: note('sale.cart', ref.registerKey, 'rejected', 'Правка корзины не применена: по продаже идёт команда. Дождитесь её исхода.'),
            }),
          )
          return
        }
        if (
          fresh.lines.some(
            (line) => quantityError(line.quantity, line.isWeight) !== null || (line.price != null && priceError(line.price) !== null),
          )
        ) {
          // Ошибку количества и цены показывает экран у строки: такую корзину служба всё равно отвергнет.
          dispatch(cartRejected({ ...ref, revision: fresh.revision }))
          return
        }
        // Пустой корзины служба не принимает (1…N позиций): убрали последнюю — продажа отменяется; продажи ещё нет — отправлять нечего.
        if (fresh.lines.length === 0 && fresh.saleId === null) {
          dispatch(cartSaved({ ...ref, revision: fresh.revision, saleId: null, version: 0 }))
          continue
        }
        const kind = fresh.lines.length === 0 ? 'cancel' : fresh.saleId === null ? 'create' : 'update'
        dispatch(
          cartSendStarted({
            ...ref,
            revision: fresh.revision,
            operationId: newUuid(),
            kind,
            // Новая продажа создаётся выбранным способом; у правки способ едет, только если кассир его сменил.
            paymentKind:
              kind === 'create' ? (fresh.paymentKind ?? seatIntentOf(select(api.getModuleState()).sale, ref).paymentKind) : kind === 'update' ? fresh.paymentKind : null,
          }),
        )
        continue
      }

      const attempt = send.attempt + 1
      dispatch(cartAttemptStarted({ ...ref, operationId: send.operationId, attempt }))
      try {
        const sale = await sendCartRequest(dispatch, ref, send, {
          forceSession,
          onVersion: (expectedVersion) => dispatch(cartSendVersioned({ ...ref, operationId: send.operationId, expectedVersion })),
        })
        forceSession = false
        if (epochOf(ref.resourceId) !== epoch) return
        if (sale === null) {
          dispatch(cartRejected({ ...ref, revision: send.revision }))
          dispatch(
            feedbackSet({ key, feedback: note('sale.cart', ref.registerKey, 'rejected', 'За этой кассой вы не работаете — начните работу в РМК.') }),
          )
          return
        }
        showSale(dispatch, ref, sale)
        dispatch(cartSaved({ ...ref, revision: send.revision, saleId: sale.isFinished ? null : sale.id, version: sale.version }))
        versionRetried = false
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        const view = errorView(error)
        const failure = classifyCartFailure(error)
        if (failure === 'retry') {
          const delay = retryDelayMs(attempt)
          dispatch(
            cartAttemptFailed({ ...ref, operationId: send.operationId, failure: failureOf(view), retryAt: new Date(Date.now() + delay).toISOString() }),
          )
          // Правку могли снять («Вернуть как на сервере») за время попытки: тогда ждать нечего.
          if (draftNow()?.send?.operationId === send.operationId) await waitForCartRetry(waitKey, delay)
          continue
        }
        if (send.kind === 'create' && view.code === 'version-mismatch' && !versionRetried) {
          // Отказ по версии сессии эффекта не имел: перечитать кассы и создать ещё раз (новый ключ) — один раз.
          versionRetried = true
          forceSession = true
          const current = draftNow()
          if (current !== undefined) {
            dispatch(cartSendStarted({ ...ref, revision: current.revision, operationId: newUuid(), kind: 'create', paymentKind: send.paymentKind }))
          }
          continue
        }
        if (failure === 'server-truth') {
          // 409: корзину изменили в другом месте или продажа уже в оплате — показать версию сервера.
          dispatch(cartDraftDropped(ref))
          dispatch(
            feedbackSet({
              key,
              feedback: {
                ...feedbackOfError('sale.cart', ref.registerKey, error),
                note:
                  view.kind === 'conflict'
                    ? 'Показана корзина с сервера — ваша правка не применена.'
                    : 'Ответ службы на правку не разобран. Показана корзина с сервера — проверьте позиции.',
              },
            }),
          )
          return
        }
        dispatch(cartRejected({ ...ref, revision: send.revision }))
        dispatch(feedbackSet({ key, feedback: feedbackOfError('sale.cart', ref.registerKey, error) }))
        // Человек успел поправить корзину, пока шёл отвергнутый запрос: новая правка уходит сама (цикл подберёт её).
      }
    }
  }

  /** Касса места свободна для правки корзины: нет черновика и команды в полёте. Иначе — отказ словами. */
  function seatReady(api: ModuleListenerApi<S>, ref: SeatWithDevice, command: string): boolean {
    const state = select(api.getModuleState())
    if (!blocksSaleCommands(saleCommandOf(state.commands, ref)) && cartDraftOf(state.cart, ref) === undefined) return true
    storeDispatch(api.dispatch)(
      feedbackSet({
        key: saleFeedbackKey(ref),
        feedback: note(command, ref.registerKey, 'rejected', 'Корзина ещё сохраняется или идёт команда — дождитесь её итога.'),
      }),
    )
    return false
  }

  /**
   * Поиск товара по строке или коду сканера — в очередь кассы (RP-SALE-FAST, шаг 7а). Очередь по порядку: код, на который сервер не ответил,
   * остаётся в ней и повторяется с теми же паузами, пока ответ не придёт, — «Ничего не найдено» говорится только по ответу сервера.
   * Каждый код в очереди — одна позиция (К-115): строка поиска освобождается в момент Enter, поэтому второй Enter по пустой строке ничего не
   * шлёт, а тот же код, пришедший снова, — это второй скан того же товара, а не дубль нажатия.
   */
  async function addFromSearch(api: ModuleListenerApi<S>, ref: SeatWithDevice, text: string, command: PendingLookup['command']): Promise<void> {
    storeDispatch(api.dispatch)(lookupQueued({ ...ref, id: newUuid(), text, command }))
    await runLookups(api, ref)
  }

  /**
   * «Отправить в кассу сейчас»: создание карточки одного товара в кассе задачей службы синхронизации (RP-SYN13; право — работать за кассой).
   * Отказ кассы — её слова вместо прежней причины, кнопка остаётся. Товар отдан кассе — ждём его карточку в кассе тем же общим поиском и
   * кладём в чек, как найденный сразу.
   */
  async function deliverToCash(api: ModuleListenerApi<S>, ref: SeatWithDevice): Promise<void> {
    const dispatch = storeDispatch(api.dispatch)
    const flight = seatKey(ref)
    const offer = seatIntentOf(select(api.getModuleState()).sale, ref).delivery
    if (offer === null || offer.phase !== 'offer' || deliveryFlights.has(flight)) return
    deliveryFlights.add(flight)
    const epoch = epochOf(ref.resourceId)
    try {
      dispatch(deliveryPhaseChanged({ ...ref, phase: 'sending' }))
      let result: CommonDeliverResult
      try {
        result = await dispatch(
          aqsiCommonDeliver.initiate({ operationId: newUuid(), resourceId: ref.resourceId, productId: offer.productId }, { track: false }),
        ).unwrap()
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        // Служба отказала (нет права, поиск недоступен) или ответа нет: слова — как есть, кнопка остаётся.
        dispatch(deliveryPhaseChanged({ ...ref, phase: 'offer' }))
        dispatch(feedbackSet({ key: saleFeedbackKey(ref), feedback: feedbackOfError('sale.delivery', ref.registerKey, error) }))
        return
      }
      if (epochOf(ref.resourceId) !== epoch) return
      if (!DELIVERED_STATES.has(result.state)) {
        dispatch(deliveryPhaseChanged({ ...ref, phase: 'offer', reason: result.message ?? DELIVERY_REFUSED_TEXT }))
        return
      }
      dispatch(deliveryPhaseChanged({ ...ref, phase: 'waiting' }))
      await waitForCard(api, ref, offer, epoch, result.jobId)
    } finally {
      deliveryFlights.delete(flight)
      retryWaits.delete(`delivery|${flight}`)
    }
  }

  /**
   * Ждать, пока карточка отправленного товара появится в кассе; пришла — товар в чек. Касса отказала — её слова сразу, не пришла за срок — кнопка
   * возвращается со словами.
   */
  async function waitForCard(api: ModuleListenerApi<S>, ref: SeatWithDevice, offer: DeliveryOffer, epoch: string, jobId: string): Promise<void> {
    const dispatch = storeDispatch(api.dispatch)
    const deadline = Date.now() + DELIVERY_WAIT_MS
    for (;;) {
      // Кассир снял сообщение или оно заменено другим товаром — ждать больше нечего.
      const current = seatIntentOf(select(api.getModuleState()).sale, ref).delivery
      if (current === null || current.productId !== offer.productId) return
      const exactCode = offer.barcode
      const answer = await commonGoods(dispatch, {
        resourceId: ref.resourceId,
        text: exactCode ?? offer.text,
        mode: exactCode === null ? 'text' : 'barcode',
        limit: exactCode === null ? LOOKUP_PAGE_SIZE : EXACT_BARCODE_PAGE_SIZE,
      })
      if (epochOf(ref.resourceId) !== epoch) return
      const hit = answer.data?.hits.find((candidate) => candidate.productId === offer.productId && candidate.good !== null)
      if (hit !== undefined) {
        dispatch(deliveryDismissed(ref))
        await applyFound(api, ref, offer.text, offer.command, foundOf(hit))
        return
      }
      // Ход отправки: отказ кассы виден сразу; сбой ответа о ходе — не отказ, ждём дальше до срока.
      const job = await dispatch(aqsiCommonDeliveryJob.initiate({ jobId }, { subscribe: false, forceRefetch: true }))
      if (epochOf(ref.resourceId) !== epoch) return
      if (job.data !== undefined && !DELIVERED_STATES.has(job.data.state)) {
        dispatch(deliveryPhaseChanged({ ...ref, phase: 'offer', reason: job.data.message ?? DELIVERY_REFUSED_TEXT }))
        return
      }
      if (Date.now() >= deadline) {
        dispatch(deliveryPhaseChanged({ ...ref, phase: 'offer', reason: DELIVERY_TIMEOUT_TEXT }))
        return
      }
      if (await waitForCommandRetry(`delivery|${seatKey(ref)}`, DELIVERY_POLL_MS)) return
    }
  }

  /** Очередь поисков кассы: по одному, с повтором при сбое связи. */
  async function runLookups(api: ModuleListenerApi<S>, ref: SeatWithDevice): Promise<void> {
    const dispatch = storeDispatch(api.dispatch)
    const seat = seatKey(ref)
    if (lookupFlights.has(seat)) return
    lookupFlights.add(seat)
    const epoch = epochOf(ref.resourceId)
    const queueNow = () => seatIntentOf(select(api.getModuleState()).sale, ref)
    try {
      for (;;) {
        if (epochOf(ref.resourceId) !== epoch) return
        const item = queueNow().lookups[0]
        if (item === undefined) return
        const attempt = item.attempt + 1
        dispatch(lookupAttempt({ ...ref, id: item.id, attempt }))
        const found = await findGood(dispatch, ref.resourceId, item.text, item.command)
        if (epochOf(ref.resourceId) !== epoch) return
        // Кассир снял поиск, пока шёл запрос.
        if (!queueNow().lookups.some((entry) => entry.id === item.id)) continue
        if (found.failure !== undefined && isRetryableError(found.failure)) {
          // Код не потерян и не «не найден»: он ждёт в очереди, а строка поиска свободна для следующего скана с момента Enter (К-115).
          const delay = retryDelayMs(attempt)
          dispatch(lookupWaiting({ ...ref, id: item.id, retryAt: new Date(Date.now() + delay).toISOString() }))
          const stop = await waitForCommandRetry(`lookup|${seat}`, delay)
          if (stop) return
          continue
        }
        dispatch(lookupDone({ ...ref, id: item.id }))
        await applyFound(api, ref, item.text, item.command, found)
      }
    } finally {
      lookupFlights.delete(seat)
      retryWaits.delete(`lookup|${seat}`)
    }
  }

  /** Найденный товар — в чек; не найден, ответ службы — отказ или корзину менять нельзя — словами. */
  async function applyFound(api: ModuleListenerApi<S>, ref: SeatWithDevice, text: string, command: PendingLookup['command'], found: FoundGood): Promise<void> {
    const dispatch = storeDispatch(api.dispatch)
    const key = saleFeedbackKey(ref)
    const { good, ambiguous, barcode } = found
    if (found.failure !== undefined) {
      // Служба отказала по существу (не сбой связи): слова службы дословно, а не «ничего не найдено».
      if (command === 'sale.scan') dispatch(searchTextChanged({ ...ref, text }))
      dispatch(feedbackSet({ key, feedback: feedbackOfError(command, ref.registerKey, found.failure) }))
      return
    }
    if (found.absent !== undefined) {
      // Товар есть в общем каталоге, а карточки в кассе нет: не «не найден», а причина дословно и кнопка «Отправить в кассу сейчас».
      if (command === 'sale.scan') dispatch(searchTextChanged({ ...ref, text }))
      dispatch(deliveryOffered({ ...ref, offer: { ...found.absent, barcode, text, command, phase: 'offer' } }))
      return
    }
    if (good === null) {
      // Сканер — в строку поиска: человек увидит, что искали, и найдёт товар руками.
      if (command === 'sale.scan') dispatch(searchTextChanged({ ...ref, text }))
      dispatch(
        feedbackSet({
          key,
          feedback: note(
            command,
            ref.registerKey,
            'rejected',
            ambiguous
              ? `Штрихкод ${text} есть у нескольких товаров — выберите нужный в «☰ Каталог».`
              : `Ничего не найдено: «${text}». Проверьте штрихкод или наименование.`,
          ),
        }),
      )
      return
    }
    if (!sellable(good)) {
      dispatch(
        feedbackSet({
          key,
          feedback: note(command, ref.registerKey, 'rejected', `${good.name}: нельзя продать — ${good.price === null ? 'нет цены в кабинете' : 'товара больше нет в кабинете'}.`),
        }),
      )
      return
    }
    const active = await dispatch(aqsiActiveSale.initiate(ref, { subscribe: false }))
    if (seatRefundOf(active.data) !== null) {
      dispatch(
        feedbackSet({
          key,
          feedback: note(command, ref.registerKey, 'rejected', `${good.name}: на кассе идёт возврат — продажу можно начать после его завершения.`),
        }),
      )
      return
    }
    const sale = saleOf(active.data)
    // Продажа не прочитана, по ней идёт команда или деньги/чек уже начаты — товар не добавляется.
    // Идёт подготовка оплаты (корзину уже сохраняют перед деньгами): позиция, добавленная сейчас, в чек оплаты не попала бы.
    if (payPreparations.has(seatKey(ref))) {
      dispatch(
        feedbackSet({
          key,
          feedback: note(command, ref.registerKey, 'rejected', `${good.name}: идёт оплата — корзину менять нельзя. Дождитесь её итога или отмените оплату.`),
        }),
      )
      return
    }
    const blocked =
      active.data === undefined ||
      blocksSaleCommands(saleCommandOf(select(api.getModuleState()).commands, ref)) ||
      (sale !== null && !canEditCart(sale))
    if (blocked) {
      dispatch(
        feedbackSet({
          key,
          feedback: note(command, ref.registerKey, 'rejected', `${good.name}: корзину сейчас менять нельзя — идёт оплата или продажа не прочитана.`),
        }),
      )
      return
    }
    dispatch(
      cartEdited({
        ...ref,
        base: cartBaseOf(sale),
        edit: {
          type: 'add',
          line: {
            productKey: good.goodId,
            name: good.name,
            barcode,
            quantity: '1',
            isWeight: good.isWeight,
            unit: good.unit,
          },
        },
      }),
    )
    // Строку поиска здесь не трогаем (К-115): ответ приходит, когда кассир уже сканирует следующий товар, — очистка стёрла бы его код.
    dispatch(feedbackSet({ key, feedback: note(command, ref.registerKey, 'done', `Добавлено: ${good.name}`) }))
  }

  return [
    // Сброс области: таймеры debounce ушедшего ресурса/пользователя не должны отправить его корзину.
    {
      predicate: (action: UnknownAction) => userScopeCleared.match(action) || resourceScopeCleared.match(action),
      effect: (action: UnknownAction) => {
        if (resourceScopeCleared.match(action)) {
          const rid = action.payload.resourceId.toLowerCase()
          resourceEpochs.set(rid, (resourceEpochs.get(rid) ?? 0) + 1)
          dropTimers((key) => belongsToResource(key, action.payload.resourceId))
        } else {
          userEpoch += 1
          dropTimers(() => true)
        }
      },
    },

    // Правка корзины → одна отправка после паузы, а не запрос на каждое нажатие (CLAUDE.md §7);
    // цена, уже решённая Enter'ом, паузы не ждёт.
    defineListener<S, ReturnType<typeof cartEdited>>(cartEdited.match, (action, api) => {
      const ref = { resourceId: action.payload.resourceId, registerKey: action.payload.registerKey, deviceId: action.payload.deviceId }
      const key = seatKey(ref)
      const previous = timers.get(key)
      if (previous !== undefined) clearTimeout(previous)
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key)
          api.dispatch(cartFlushRequested(ref))
        }, action.payload.edit.type === 'price' ? CART_PRICE_DEBOUNCE_MS : CART_DEBOUNCE_MS),
      )
    }),

    // Очередь корзины: одна отправка за раз, повтор тем же ключом, пока сервер не ответил (RP-SALE-FAST, К-104).
    defineListener<S, ReturnType<typeof cartFlushRequested>>(cartFlushRequested.match, async (action, api) => {
      const ref = action.payload
      const flight = seatKey(ref)
      if (cartFlights.has(flight)) return
      cartFlights.add(flight)
      try {
        await runCartQueue(api, ref)
      } finally {
        cartFlights.delete(flight)
      }
    }),

    // «Повторить сейчас»: не ждать паузы между попытками.
    defineListener<S, ReturnType<typeof cartRetryRequested>>(cartRetryRequested.match, (action) => {
      cartWaits.get(seatKey(action.payload))?.()
    }),

    // «Вернуть как на сервере»: повтор останавливается, корзину показывает сервер (неподтверждённый запрос мог дойти — продажа перечитывается).
    defineListener<S, ReturnType<typeof cartDiscardRequested>>(cartDiscardRequested.match, (action, api) => {
      const ref = action.payload
      storeDispatch(api.dispatch)(cartDraftDropped(ref))
      cartWaits.get(seatKey(ref))?.()
      rereadSeat(storeDispatch(api.dispatch), ref)
    }),

    // F5: несохранённые правки вернулись из sessionStorage — очередь отправляет их сама, тем же ключом, если отправка уже шла.
    defineListener<S, ReturnType<typeof cartDraftsRestored>>(cartDraftsRestored.match, (action, api) => {
      for (const draft of Object.values(action.payload)) {
        api.dispatch(cartFlushRequested({ resourceId: draft.resourceId, registerKey: draft.registerKey, deviceId: draft.deviceId }))
      }
    }),

    defineListener<S, ReturnType<typeof salePayRequested>>(salePayRequested.match, (action, api) =>
      runSaleCommand('pay', action.payload, api),
    ),
    defineListener<S, ReturnType<typeof saleFiscalizeRequested>>(saleFiscalizeRequested.match, (action, api) =>
      runSaleCommand('fiscalize', action.payload, api),
    ),
    defineListener<S, ReturnType<typeof saleCancelRequested>>(saleCancelRequested.match, (action, api) =>
      runSaleCommand('cancel', action.payload, api),
    ),
    // «Отменить» оплату картой — и у терминала, и когда исход оплаты выясняется или ждёт сверки. Служба сначала ЧИТАЕТ состояние у
    // кабинета: оплата уже прошла (карту приложили, пока не было связи) — отмена невозможна, оплата подтверждается; иначе операция снимается с
    // терминала. Просьба безопасна для повтора и не создаёт ждущей команды: итог читается продажей и операцией, а не памятью клиента
    // (никаких «неизвестный исход, повтор запрещён» из-за отмены).
    defineListener<S, ReturnType<typeof salePaymentCancelRequested>>(salePaymentCancelRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const ref = { resourceId: action.payload.resourceId, registerKey: action.payload.registerKey, deviceId: action.payload.deviceId }
      const flight = ridKey(ref.resourceId, `cancel-payment:${action.payload.saleId.toLowerCase()}`)
      if (cancellingPayments.has(flight)) return
      cancellingPayments.add(flight)
      const command = 'sale.cancel-payment'
      const saleId = action.payload.saleId
      const say = (feedback: CommandFeedback): void => sayInPaymentWindow(dispatch, ref, saleId, feedback)
      const attemptRef = { resourceId: ref.resourceId, registerKey: ref.registerKey, saleId, kind: 'Card' as const }
      const seat = seatKey(ref)
      const epoch = epochOf(ref.resourceId)
      // Отмену просит кассир (К-94): отказ этой попытки — тихий, окна «Не оплачено» нет, в каком бы порядке ни пришли ответы.
      dispatch(paymentCancelPressed(attemptRef))
      // Один ключ на все попытки: связи нет — отмена ждёт в очереди и уходит ТЕМ ЖЕ запросом, как только связь появится (RP-SALE-FAST, шаг 6).
      const intent = newCommandIntent()
      try {
        for (let attempt = 1; ; attempt += 1) {
          dispatch(paymentCancelQueued({ ...attemptRef, queued: false }))
          say(note(command, ref.registerKey, 'sending', attempt === 1 ? 'Проверяем оплату и отменяем её…' : `Проверяем оплату и отменяем её… (попытка ${attempt})`))
          try {
            const sale = await dispatch(
              aqsiSaleCancelPayment.initiate({ ...ref, saleId, intent, expectedVersion: action.payload.expectedVersion }, { track: false }),
            ).unwrap()
            showSale(dispatch, ref, sale)
            const done =
              sale.payment === 'Failed'
                ? 'Оплата отменена. Деньги не списаны'
                : sale.payment === 'Succeeded'
                  ? 'Отмена невозможна: оплата уже прошла. Деньги получены — пробейте чек'
                  : sale.payment === 'Unknown'
                    ? 'Пока неясно, прошла ли оплата: служба продолжает проверять кассу и кабинет'
                    : 'Отмена отправлена на терминал'
            say(note(command, ref.registerKey, 'done', done))
            return
          } catch (error) {
            if (epochOf(ref.resourceId) !== epoch) return
            if (isRetryableError(error)) {
              // Нет ответа: просьба безопасна для повтора. Пока оплата ещё идёт, отмена ждёт связи в очереди и уходит тем же ключом.
              const active = saleOf((await dispatch(aqsiActiveSale.initiate(ref, { subscribe: false }))).data)
              if (active !== null && sameId(active.id, saleId) && ['InProgress', 'Unknown'].includes(active.payment)) {
                dispatch(paymentCancelQueued({ ...attemptRef, queued: true }))
                say(note(command, ref.registerKey, 'sending', CANCEL_QUEUED_TEXT))
                const stop = await waitForCommandRetry(`cancel-payment|${seat}`, retryDelayMs(attempt))
                if (epochOf(ref.resourceId) !== epoch) return
                if (!stop) continue
              }
              dispatch(paymentCancelQueued({ ...attemptRef, queued: false }))
            }
            // Отказ или остановленный повтор: состояние читается заново, оплата продолжается сама — человеку остаётся текст причины.
            rereadSeat(dispatch, ref)
            say(feedbackOfError(command, ref.registerKey, error))
            return
          }
        }
      } finally {
        retryWaits.delete(`cancel-payment|${seat}`)
        cancellingPayments.delete(flight)
      }
    }),

    // Слип покупки нашёлся после отмены оплаты (внимание `late-payment-slip`): «Оплаты не было» / «Оплата прошла — пробить чек».
    // Как повтор чека: служба идемпотентна по ключу и версии продажи, поэтому в sessionStorage команда не пишется. Отказ 409
    // (`late-slip-not-adoptable` / `late-slip-bound` / `late-slip-none`) несёт русский текст — он показывается дословно и не исчезает сам.
    defineListener<S, ReturnType<typeof saleLatePaymentRequested>>(saleLatePaymentRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { paid, saleId, expectedVersion, background, ...ref } = action.payload
      const flight = ridKey(ref.resourceId, `late-payment:${saleId.toLowerCase()}`)
      if (latePaymentFlights.has(flight)) return
      latePaymentFlights.add(flight)
      const key = saleFeedbackKey(ref)
      const command = 'sale.late-payment'
      // Отложенная продажа из «Чеков в фоне» — не окно оплаты активной продажи: её итог — в общем итоге кассы.
      const say = (feedback: CommandFeedback): void => {
        if (background === true) dispatch(feedbackSet({ key, feedback }))
        else sayInPaymentWindow(dispatch, ref, saleId, feedback)
      }
      const epoch = epochOf(ref.resourceId)
      say(note(command, ref.registerKey, 'sending', paid ? 'Фиксируем оплату и отправляем чек…' : 'Фиксируем, что оплаты не было…'))
      try {
        const sale = await dispatch(
          aqsiSaleLatePayment.initiate({ ...ref, saleId, intent: newCommandIntent(), expectedVersion, paid }, { track: false }),
        ).unwrap()
        if (epochOf(ref.resourceId) !== epoch) return
        showSale(dispatch, ref, sale)
        say(note(command, ref.registerKey, 'done', paid ? 'Оплата подтверждена. Чек отправляется — ход виден в окне продажи' : 'Принято: оплаты не было. Деньги не списаны'))
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        // Отказ или обрыв: продажа перечитывается, текст причины остаётся на экране.
        rereadSeat(dispatch, ref)
        say(feedbackOfError(command, ref.registerKey, error))
      } finally {
        latePaymentFlights.delete(flight)
      }
    }),

    defineListener<S, ReturnType<typeof saleIssuedOutsideRequested>>(saleIssuedOutsideRequested.match, (action, api) => {
      const { reason, receiptId, ...ref } = action.payload
      // Причина обязательна (договор: 1…2000): пустую не отправляем — служба всё равно отвергнет.
      if (reason.trim() === '') return
      return runSaleCommand('outside', ref, api, { reason: reason.trim(), receiptId })
    }),

    // RP-FB35 K7: «Чека нет — пробить заново». Не опасная команда в смысле `saleCommands`: служба идемпотентна по ключу и версии продажи,
    // поэтому в sessionStorage она не пишется; двойное нажатие гасит набор в памяти, а потерянный ответ читается продажей.
    defineListener<S, ReturnType<typeof saleReceiptRetryRequested>>(saleReceiptRetryRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { reason, checked, saleId, expectedVersion, ...ref } = action.payload
      const flight = ridKey(ref.resourceId, `receipt-retry:${saleId.toLowerCase()}`)
      if (retryingReceipts.has(flight)) return
      const command = 'sale.retry'
      const say = (feedback: CommandFeedback): void => sayInPaymentWindow(dispatch, ref, saleId, feedback)
      const state = select(api.getModuleState())
      if (blocksSaleCommands(saleCommandOf(state.commands, ref))) {
        say(note(command, ref.registerKey, 'rejected', 'Прошлая команда по этой продаже ещё не завершена или её исход неизвестен. Проверьте статус.'))
        return
      }
      retryingReceipts.add(flight)
      const epoch = epochOf(ref.resourceId)
      say(note(command, ref.registerKey, 'sending', 'Повторяем проведение…'))
      try {
        const sale = await dispatch(
          aqsiSaleReceiptRetry.initiate(
            { ...ref, saleId, intent: newCommandIntent(), expectedVersion, reason: reason?.trim() || undefined, checked: checked?.trim() || undefined },
            { track: false },
          ),
        ).unwrap()
        if (epochOf(ref.resourceId) !== epoch) return
        dispatch(retryClosed(ref))
        showSale(dispatch, ref, sale)
        // Служба могла принять команду, но выждать (срок команды, задержка журнала): причина и время «повторим в ЧЧ:ММ» приходят в чтении продажи (RP-FB35 N2).
        if (!sale.isFinished) rereadSeat(dispatch, ref)
        const done = sale.isFinished ? 'Проведение повторено — ход виден ниже' : 'Команда принята: служба доведёт проведение сама — ход виден ниже'
        say(note(command, ref.registerKey, 'done', done))
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        // Отказ (409/422) эффекта не имел; обрыв — исход читается продажей, повторять нельзя.
        rereadSeat(dispatch, ref)
        const result = feedbackOfError(command, ref.registerKey, error)
        say(result.phase === 'unknown' ? { ...result, note: 'Ответ не получен: неизвестно, ушёл ли чек. Продажа перечитана — повторять нельзя.' } : result)
      } finally {
        retryingReceipts.delete(flight)
      }
    }),

    // RP-FB35 K9: «Отложить продажу». Как повтор чека: не «опасная» команда `saleCommands`, служба идемпотентна по ключу и версии продажи.
    defineListener<S, ReturnType<typeof saleParkRequested>>(saleParkRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { reason, saleId, expectedVersion, ...ref } = action.payload
      const flight = ridKey(ref.resourceId, `park:${saleId.toLowerCase()}`)
      if (parkFlights.has(flight)) return
      const command = 'sale.park'
      const say = (feedback: CommandFeedback): void => sayInPaymentWindow(dispatch, ref, saleId, feedback)
      if (blocksSaleCommands(saleCommandOf(select(api.getModuleState()).commands, ref))) {
        say(note(command, ref.registerKey, 'rejected', 'Прошлая команда по этой продаже ещё не завершена или её исход неизвестен. Проверьте статус.'))
        return
      }
      parkFlights.add(flight)
      const epoch = epochOf(ref.resourceId)
      say(note(command, ref.registerKey, 'sending', 'Продажа откладывается…'))
      try {
        const sale = await dispatch(
          aqsiSalePark.initiate(
            { ...ref, saleId, intent: newCommandIntent(), expectedVersion, reason: reason !== null && reason.trim() !== '' ? reason.trim() : null },
            { track: false },
          ),
        ).unwrap()
        if (epochOf(ref.resourceId) !== epoch) return
        dispatch(parkClosed(ref))
        showSale(dispatch, ref, sale)
        say(note(command, ref.registerKey, 'done', 'Продажа отложена: деньги взяты, чек не пробит. Она в «Чеки» → «Отложенные продажи».'))
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        rereadSeat(dispatch, ref)
        const result = feedbackOfError(command, ref.registerKey, error)
        say(result.phase === 'unknown' ? { ...result, note: 'Ответ не получен: неизвестно, отложена ли продажа. Продажа перечитана — повторять не нужно.' } : result)
      } finally {
        parkFlights.delete(flight)
      }
    }),

    // RP-FB35 K9: «Вернуть на кассу» из списка отложенных. Занята касса — 409 `register-busy`, текст службы дословно.
    defineListener<S, ReturnType<typeof saleUnparkRequested>>(saleUnparkRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { saleId, expectedVersion, ...ref } = action.payload
      const flight = ridKey(ref.resourceId, `unpark:${saleId.toLowerCase()}`)
      if (parkFlights.has(flight)) return
      const key = saleFeedbackKey(ref)
      const command = 'sale.unpark'
      parkFlights.add(flight)
      const epoch = epochOf(ref.resourceId)
      dispatch(feedbackSet({ key, feedback: note(command, ref.registerKey, 'sending', 'Продажа возвращается на кассу…') }))
      try {
        const sale = await dispatch(
          aqsiSaleUnpark.initiate({ ...ref, saleId, intent: newCommandIntent(), expectedVersion }, { track: false }),
        ).unwrap()
        if (epochOf(ref.resourceId) !== epoch) return
        showSale(dispatch, ref, sale)
        // Быстрая оплата: продажа снова на кассе — её ведёт окно оплаты, панель «Чек» о ней больше не говорит.
        dispatch(fastReceiptDismissed({ resourceId: ref.resourceId, registerKey: ref.registerKey, saleId }))
        dispatch(feedbackSet({ key, feedback: note(command, ref.registerKey, 'done', 'Продажа возвращена на кассу — действия с чеком в окне РМК') }))
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        dispatch(saleApi.util.invalidateTags([resourceTag(AQSI_TAGS.saleParked, ref.resourceId, 'LIST')]))
        const result = feedbackOfError(command, ref.registerKey, error)
        dispatch(
          feedbackSet({
            key,
            feedback:
              result.phase === 'unknown'
                ? { ...result, note: 'Ответ не получен: неизвестно, вернулась ли продажа. Список перечитан — повторять не нужно.' }
                : result,
          }),
        )
      } finally {
        parkFlights.delete(flight)
      }
    }),

    defineListener<S, ReturnType<typeof salePaymentKindRequested>>(
      salePaymentKindRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const { kind, sale, ...ref } = action.payload
        const state = select(api.getModuleState())
        const say = (feedback: CommandFeedback): void => sayInPaymentWindow(dispatch, ref, sale?.id ?? null, feedback, kind)
        if (sale === null) {
          dispatch(paymentKindChosen({ ...ref, kind }))
          return
        }
        if (blocksSaleCommands(saleCommandOf(state.commands, ref)) || cartDraftOf(state.cart, ref) !== undefined) {
          say(note('sale.payment-kind', ref.registerKey, 'rejected', 'Дождитесь сохранения корзины и итога команды.'))
          return
        }
        const flight = seatKey(ref)
        if (switching.has(flight)) return
        switching.add(flight)
        const epoch = epochOf(ref.resourceId)
        say(note('sale.payment-kind', ref.registerKey, 'sending', 'Меняется способ оплаты…'))
        try {
          const cancelled = await dispatch(
            aqsiSaleCancel.initiate(
              { ...ref, saleId: sale.id, intent: newCommandIntent(), expectedVersion: sale.version },
              { track: false },
            ),
          ).unwrap()
          if (epochOf(ref.resourceId) !== epoch) return
          showSale(dispatch, ref, cancelled)
          dispatch(paymentKindChosen({ ...ref, kind }))
          dispatch(
            cartRestored({
              ...ref,
              lines: sale.lines.map((line) => ({
                productKey: line.productKey,
                name: '',
                barcode: line.barcode,
                quantity: line.quantity,
                isWeight: null,
                unit: null,
                price: line.price ?? null,
              })),
            }),
          )
          say(note('sale.payment-kind', ref.registerKey, 'done', 'Способ оплаты изменён'))
          api.dispatch(cartFlushRequested(ref))
        } catch (error) {
          if (epochOf(ref.resourceId) !== epoch) return
          const result = feedbackOfError('sale.payment-kind', ref.registerKey, error)
          say(result.phase === 'unknown' ? { ...result, note: 'Ответ на отмену не получен. Отмена не повторяется — продажа перечитана, проверьте её.' } : result)
        } finally {
          switching.delete(flight)
        }
      },
    ),

    defineListener<S, ReturnType<typeof saleStatusCheckRequested>>(saleStatusCheckRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId, registerKey, deviceId, operationId } = action.payload
      const reads: Promise<unknown>[] = [
        dispatch(aqsiActiveSale.initiate({ resourceId, registerKey, deviceId }, { subscribe: false, forceRefetch: true })),
      ]
      if (operationId !== null) {
        reads.push(dispatch(aqsiSaleOperation.initiate({ resourceId, operationId }, { subscribe: false, forceRefetch: true })))
      }
      await Promise.all(reads)
    }),

    defineListener<S, ReturnType<typeof saleReconcileRequested>>(saleReconcileRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId, registerKey, operationId, expectedVersion, automatic, saleId = null } = action.payload
      const flight = ridKey(resourceId, operationId.toLowerCase())
      if (reconciling.has(flight)) return
      reconciling.add(flight)
      // Сверка по нажатию — шаг окна оплаты: итог в окне; опрос хода молчит.
      const say = (feedback: CommandFeedback): void => {
        if (!automatic) sayInPaymentWindow(dispatch, { resourceId, registerKey }, saleId, feedback)
      }
      say(note('sale.reconcile', registerKey, 'sending', null))
      try {
        const status = await dispatch(
          aqsiSaleReconcile.initiate(
            { resourceId, operationId, expectedVersion, intent: newCommandIntent() },
            { track: false },
          ),
        ).unwrap()
        void dispatch(saleApi.util.upsertQueryData('aqsiSaleOperation', { resourceId, operationId }, status))
        say(note('sale.reconcile', registerKey, 'done', 'Сверка выполнена'))
      } catch (error) {
        // Опрос хода молчит: следующий тик прочитает операцию заново (endpoint перечитывает её при отказе).
        say(feedbackOfError('sale.reconcile', registerKey, error))
      } finally {
        reconciling.delete(flight)
      }
    }),

    // Статус операции (чтение, сверка или ручная сверка) изменил деньги/чек — перечитать продажу места.
    {
      predicate: (action: UnknownAction) =>
        aqsiSaleOperation.matchFulfilled(action) ||
        aqsiSaleReconcile.matchFulfilled(action) ||
        aqsiOperationResolve.matchFulfilled(action),
      effect: (action: UnknownAction, api: ModuleListenerApi<S>) => {
        let status: AqsiSaleOperation
        let resourceId: string
        if (aqsiSaleOperation.matchFulfilled(action)) {
          status = action.payload
          resourceId = action.meta.arg.originalArgs.resourceId
        } else if (aqsiSaleReconcile.matchFulfilled(action)) {
          status = action.payload
          resourceId = action.meta.arg.originalArgs.resourceId
        } else if (aqsiOperationResolve.matchFulfilled(action)) {
          status = action.payload
          resourceId = action.meta.arg.originalArgs.resourceId
        } else {
          return
        }
        if (status.state === 'Pending' || status.state === 'Sent') return
        const dispatch = storeDispatch(api.dispatch)
        const ref = { resourceId, registerKey: status.registerKey }
        if (status.kind === 'receipt' && status.state === 'Succeeded') {
          // Быстрая оплата: чек фоновой продажи пробит — итог в панели «Чек», а окно итога поверх новой корзины не открывается.
          if (isFastReceiptPending(select(api.getModuleState()).fast, ref, status.saleId)) dispatch(fastReceiptDone({ ...ref, saleId: status.saleId }))
          else dispatch(saleFinishedShown({ ...ref, saleId: status.saleId }))
          dispatch(saleApi.util.invalidateTags([resourceTag(AQSI_TAGS.sale, resourceId, status.saleId)]))
        }
        rereadSeat(dispatch, ref)
      },
    },

    // Чтение продажи — единственный способ узнать исход команды без ответа: дошла ли она до службы.
    {
      predicate: (action: UnknownAction) => aqsiActiveSale.matchFulfilled(action),
      effect: (action: UnknownAction, api: ModuleListenerApi<S>) => {
        if (!aqsiActiveSale.matchFulfilled(action)) return
        const ref = action.meta.arg.originalArgs
        const command = saleCommandOf(select(api.getModuleState()).commands, ref)
        if (command === undefined || command.phase === 'sending') return
        // Возврат на кассе вместо продажи — та продажа уже завершена или отменена.
        const sale = saleOf(action.payload)
        const dispatch = storeDispatch(api.dispatch)
        const commandRef = { ...ref, operationId: command.operationId }
        if (sale === null || !sameId(sale.id, command.saleId) || saleKnowsCommand(sale, command)) {
          // Продажа завершена/сменилась или уже несёт нашу операцию: дальше правду говорит она сама.
          dispatch(saleCommandSettled(commandRef))
          return
        }
        if (command.phase === 'not-arrived') return
        // Срок «не дошла» — от последнего обмена с сервером по этой команде (начало последней попытки или ответ), а не от нажатия.
        const lastExchange = Date.parse(command.activityAt)
        const readAt = action.meta.fulfilledTimeStamp
        if (Number.isFinite(lastExchange) && readAt - lastExchange >= NOT_ARRIVED_AFTER_MS) {
          dispatch(saleCommandNotArrived(commandRef))
        }
      },
    },

    // Чтение показало идущую оплату или чек: попытка живая — её отказ окно объявит и после F5 (RP-PAY-ONE, вопрос 1). Без записанной
    // попытки (другая вкладка, чистая сессия) — подхватывается по продаже тем же ключом, каким окно её уже показывает.
    {
      predicate: (action: UnknownAction) => aqsiActiveSale.matchFulfilled(action),
      effect: (action: UnknownAction, api: ModuleListenerApi<S>) => {
        if (!aqsiActiveSale.matchFulfilled(action)) return
        const sale = saleOf(action.payload)
        if (sale === null || sale.isFinished) return
        const running = sale.payment === 'InProgress' || sale.payment === 'Unknown' || sale.fiscal === 'InProgress' || sale.fiscal === 'Unknown'
        if (!running) return
        const ref = action.meta.arg.originalArgs
        const attempts = select(api.getModuleState()).attempts
        const attempt = attempts === undefined ? undefined : paymentAttemptOf(attempts, ref)
        if (attempt !== undefined && attempt.live && attempt.saleId !== null && sameId(attempt.saleId, sale.id)) return
        storeDispatch(api.dispatch)(
          paymentAttemptLive({ resourceId: ref.resourceId, registerKey: ref.registerKey, saleId: sale.id, kind: sale.paymentKind }),
        )
      },
    },

    // Быстрая оплата: деньги взяты, чек у кассы — продажа уходит в фон. Решает чтение продажи, а не таймер.
    {
      predicate: (action: UnknownAction) => aqsiActiveSale.matchFulfilled(action),
      effect: async (action: UnknownAction, api: ModuleListenerApi<S>) => {
        if (!aqsiActiveSale.matchFulfilled(action)) return
        const sale = saleOf(action.payload)
        if (sale !== null) await handOffToBackground(api, action.meta.arg.originalArgs, sale)
      },
    },

    // Быстрая оплата: список отложенных продаж обновился — продажа, ушедшая в фон, могла закончиться (чек пробит) или вернуться на кассу.
    {
      predicate: (action: UnknownAction) => aqsiParkedSales.matchFulfilled(action),
      effect: async (action: UnknownAction, api: ModuleListenerApi<S>) => {
        if (!aqsiParkedSales.matchFulfilled(action)) return
        const fast = select(api.getModuleState()).fast
        if (fast === undefined) return
        const dispatch = storeDispatch(api.dispatch)
        const { resourceId, deviceId } = action.meta.arg.originalArgs
        const rows = action.payload
        for (const notes of Object.values(fast.bySeat)) {
          for (const note of notes) {
            if (note.doneAt !== null || note.deviceId !== deviceId || !sameId(note.resourceId, resourceId)) continue
            if (rows.some((row) => sameId(row.id, note.saleId))) continue
            // Продажи нет в списке отложенных: она закончена или вернулась на кассу — это знает только чтение самой продажи.
            try {
              const sale = await dispatch(aqsiSale.initiate({ resourceId, saleId: note.saleId }, { subscribe: false, forceRefetch: true })).unwrap()
              const ref = { resourceId, registerKey: note.registerKey, saleId: note.saleId }
              if (sale.isFinished && (sale.fiscal === 'Succeeded' || sale.fiscal === 'IssuedOutside')) dispatch(fastReceiptDone(ref))
              else if (sale.isFinished || !sale.isParked) dispatch(fastReceiptDismissed(ref))
            } catch {
              // Не прочитали — следующее чтение списка спросит снова.
            }
          }
        }
      },
    },

    // Код со сканера: товар ищет служба по всему снимку каталога ЭТОГО кабинета (UI-RMK, `search=`).
    defineListener<S, ReturnType<typeof saleBarcodeScanned>>(saleBarcodeScanned.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId } = action.payload
      const code = action.payload.code.trim()
      if (code === '' || code.length > 128) return
      const own = await dispatch(aqsiMySession.initiate({ resourceId }, { subscribe: false }))
      const mine = own.data
      if (mine == null) return
      await addFromSearch(api, { resourceId, registerKey: mine.registerKey, deviceId: mine.deviceId }, code, 'sale.scan')
    }),

    // «Отправить в кассу сейчас»: раздача одного товара, затем он в чек.
    defineListener<S, ReturnType<typeof saleDeliveryRequested>>(saleDeliveryRequested.match, async (action, api) => {
      await deliverToCash(api, action.payload)
    }),

    // Сообщение «ещё не в кассе» снято: ожидание карточки прекращается.
    defineListener<S, ReturnType<typeof deliveryDismissed>>(deliveryDismissed.match, (action) => {
      retryWaits.get(`delivery|${seatKey(action.payload)}`)?.(true)
    }),

    // «Снять» ожидающий поиск: код выпадает из очереди; если он ждал паузу, очередь идёт дальше сразу.
    defineListener<S, ReturnType<typeof lookupCancelRequested>>(lookupCancelRequested.match, (action, api) => {
      const { id, ...ref } = action.payload
      storeDispatch(api.dispatch)(lookupDone({ ...ref, id }))
      retryWaits.get(`lookup|${seatKey(ref)}`)?.(false)
    }),

    // UI-RMK: Enter в строке поиска — по наименованию первый найденный в чек; код из цифр (сканер-клавиатура) — только товар с ровно этим
    // штрихкодом или артикулом (К-115). Строку поиска к этому моменту уже освободил редьюсер: следующий скан идёт в пустую строку.
    defineListener<S, ReturnType<typeof saleSearchSubmitted>>(saleSearchSubmitted.match, async (action, api) => {
      const { text, ...ref } = action.payload
      const query = text.trim()
      if (query === '' || query.length > 128) return
      await addFromSearch(api, ref, query, 'sale.search')
    }),

    // UI-RMK: «Наличными» / «Картой» одним нажатием. Кнопка не зависит от того, сохранена ли корзина: окно открыто с первой секунды,
    // оплата ждёт очередь корзины и только потом идёт прежним путём (RP-SALE-FAST, шаг 4, К-105).
    defineListener<S, ReturnType<typeof salePayWithRequested>>(salePayWithRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { kind, ...ref } = action.payload
      const flightKey = seatKey(ref)
      // Двойное нажатие, пока подготовка идёт, — одна подготовка.
      if (payPreparations.has(flightKey)) return
      const prep: PayPreparation = { cancelled: false, settled: null, resolve: null }
      payPreparations.set(flightKey, prep)
      const clickedAt = Date.now()
      const epoch = epochOf(ref.resourceId)
      const command = kind === 'Card' ? 'sale.pay-card' : 'sale.pay-cash'
      const aborted = () => prep.cancelled || epochOf(ref.resourceId) !== epoch
      const say = (feedback: CommandFeedback, saleId: string | null = null): void => {
        if (!prep.cancelled) sayInPaymentWindow(dispatch, ref, saleId, feedback, kind)
      }
      const refuse = (text: string, saleId: string | null = null): void => {
        say(note(command, ref.registerKey, 'rejected', text), saleId)
        if (saleId !== null) trace.mark('done', { saleId, outcome: 'refused' })
      }
      const saving = (saleId: string | null = null): void => say(note(SAVE_COMMAND, ref.registerKey, 'sending', PAY_SAVING_CART_TEXT), saleId)
      const stateNow = () => select(api.getModuleState())
      const draftNow = () => cartDraftOf(stateNow().cart, ref)
      /** Продажа кассы из кэша: корзина сохранена, свежий ответ службы уже положен туда `showSale`. */
      const saleNow = async (): Promise<AqsiSale | null> => saleOf((await dispatch(aqsiActiveSale.initiate(ref, { subscribe: false }))).data)
      try {
        if (blocksSaleCommands(saleCommandOf(stateNow().commands, ref))) {
          refuse('Прошлая команда по этой продаже ещё не завершена или её исход неизвестен. Проверьте статус — повторная команда запрещена.')
          return
        }
        // Продажи ещё нет (первая позиция создаётся): она появится с выбранным способом оплаты.
        if ((await saleNow()) === null) dispatch(paymentKindChosen({ ...ref, kind }))
        if (aborted()) return

        // 0. Быстрая оплата: касса не примет новую операцию, пока печатается чек предыдущей продажи, — ждём его конца. Проблемный чек не
        // ждётся, а требует решения: оплата не начинается, деньги не уходят.
        if (await fastPaymentOn(dispatch, ref)) {
          if (aborted()) return
          let ahead: ReceiptsAhead
          try {
            ahead = receiptsAheadOf(await dispatch(aqsiParkedSales.initiate({ resourceId: ref.resourceId, deviceId: ref.deviceId }, { subscribe: false, forceRefetch: true })).unwrap(), Date.now())
          } catch (error) {
            if (aborted()) return
            refuse(`Не удалось проверить чек предыдущей продажи: ${errorView(error).message} Деньги не списаны.`)
            return
          }
          if (aborted()) return
          if (ahead.printing > 0) {
            say(note(WAIT_RECEIPT_COMMAND, ref.registerKey, 'sending', PAY_WAITING_RECEIPT_TEXT))
            await untilReceiptsPrinted(dispatch, ref, prep)
            if (aborted()) return
          }
        }

        // 1. Очередь корзины: сначала сохраняется то, что набрано и поправлено (цена, количество, контакт).
        if (draftNow() !== undefined) {
          saving()
          api.dispatch(cartFlushRequested(ref))
          await untilCartSettled(api, ref, prep)
          if (aborted()) return
          if (draftNow() !== undefined) {
            refuse('Служба не приняла правку корзины — исправьте её или верните как на сервере. Деньги не списаны.')
            return
          }
        }
        let sale = await saleNow()
        if (aborted()) return
        if (sale === null) {
          refuse('В чеке нет позиций — корзину изменили на сервере. Проверьте чек. Деньги не списаны.')
          return
        }
        if (!canEditCart(sale)) {
          refuse('Оплата или чек по этой продаже уже начаты — корзину и способ оплаты менять нельзя. Проверьте ход продажи.', sale.id)
          return
        }

        trace.mark('click', { scope: ref.resourceId, saleId: sale.id, at: clickedAt, meta: { kind: kind === 'Card' ? 'card' : 'cash' } })
        trace.mark('cart-queue-wait', { saleId: sale.id, durationMs: Date.now() - clickedAt })

        // 2. Касса получит оплату: сначала проверка связи — если она включена на службе и «на связи» не подтверждено (UI-RMK-FLOW 3.9).
        // Не ответила — оплата не начинается, корзина цела. «Тот же» кассой не опрашивается в простое.
        say(note(command, ref.registerKey, 'sending', PAY_CHECKING_LINK_TEXT), sale.id)
        const probedAt = Date.now()
        const gate = await probeRegister(dispatch, ref.resourceId, ref.deviceId, 'payment')
        trace.mark('probe', { saleId: sale.id, durationMs: Date.now() - probedAt, outcome: gate.proceed ? 'ok' : 'refused' })
        if (aborted()) return
        if (!gate.proceed) {
          refuse(gate.message ?? 'Касса не ответила.', sale.id)
          return
        }

        // 3. Способ оплаты меняется вместе с корзиной (те же позиции) — тоже через очередь: продажа та же, денег и чека ещё не было.
        if (sale.paymentKind !== kind) {
          saving(sale.id)
          dispatch(cartEdited({ ...ref, base: cartBaseOf(sale), edit: { type: 'payment-kind', kind } }))
          api.dispatch(cartFlushRequested(ref))
          await untilCartSettled(api, ref, prep)
          if (aborted()) return
          if (draftNow() !== undefined) {
            refuse('Служба не приняла смену способа оплаты. Деньги не списаны.', sale.id)
            return
          }
          sale = await saleNow()
          if (aborted()) return
          if (sale === null || !canEditCart(sale)) {
            refuse('Продажа изменилась на сервере. Проверьте чек. Деньги не списаны.')
            return
          }
          if (sale.paymentKind !== kind) {
            // Прежняя служба поле не знает и молча оставила прежний способ: оплачивать не тем способом нельзя.
            refuse('Служба кассы не сменила способ оплаты — обновите службу кассы (нужна 0.22.0).', sale.id)
            return
          }
          dispatch(paymentKindChosen({ ...ref, kind }))
        }

        // 4. Одна команда оплаты с версией продажи, которую знает служба.
        await runSaleCommand('pay', { ...ref, saleId: sale.id, expectedVersion: sale.version }, api)
      } finally {
        if (payPreparations.get(flightKey) === prep) payPreparations.delete(flightKey)
      }
    }),

    // «Отменить» в подготовке оплаты: деньги не уходили — попытка закрывается, оплата не начнётся. Команда оплаты уже создана — поздно:
    // дальше действует «Отменить» оплату (по состоянию на терминале).
    defineListener<S, ReturnType<typeof salePreparationCancelRequested>>(salePreparationCancelRequested.match, (action, api) => {
      const ref = action.payload
      const flightKey = seatKey(ref)
      const pending = saleCommandOf(select(api.getModuleState()).commands, ref)
      if (pending !== undefined) {
        // Оплата уже отправлена (в пути или ждёт повтора): остановить повтор. Исход неизвестен — окно переходит к проверке на кассе, а
        // запрос, который ещё в пути, дойдёт сам и покажет себя в продаже; повторной оплаты не будет.
        if (pending.kind !== 'pay' || pending.phase !== 'sending') return
        commandStops.add(flightKey)
        storeDispatch(api.dispatch)(saleCommandUnknown({ resourceId: ref.resourceId, registerKey: ref.registerKey, operationId: pending.operationId }))
        retryWaits.get(`pay|${flightKey}`)?.(true)
        return
      }
      const prep = payPreparations.get(flightKey)
      if (prep === undefined) return
      prep.cancelled = true
      payPreparations.delete(flightKey)
      storeDispatch(api.dispatch)(paymentClosed(ref))
      prep.resolve?.()
    }),

    // Очередь корзины закончила работу (сохранила, отказала, правку сняли) — подготовка оплаты, которая её ждёт, идёт дальше.
    {
      predicate: (action: UnknownAction) =>
        cartSaved.match(action) ||
        cartRejected.match(action) ||
        cartDraftDropped.match(action) ||
        saleSeatCleared.match(action) ||
        saleFinishedShown.match(action) ||
        aqsiParkedSales.matchFulfilled(action) ||
        fastReceiptDone.match(action) ||
        userScopeCleared.match(action) ||
        resourceScopeCleared.match(action),
      effect: () => {
        for (const prep of payPreparations.values()) if (prep.resolve !== null && prep.settled !== null && prep.settled()) prep.resolve()
      },
    },

    // Связь с сервером вернулась: очередь корзины не ждёт конца паузы, а повторяет сразу.
    {
      predicate: (action: UnknownAction) => connectionRestored.match(action),
      effect: () => {
        for (const wake of [...cartWaits.values()]) wake()
        for (const wake of [...retryWaits.values()]) wake(false)
      },
    },

    // UI-RMK: «Отложить».
    defineListener<S, ReturnType<typeof saleHoldRequested>>(saleHoldRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { saleId, expectedVersion, ...ref } = action.payload
      const key = saleFeedbackKey(ref)
      if (!seatReady(api, ref, 'sale.hold')) return
      const epoch = epochOf(ref.resourceId)
      try {
        const result = await dispatch(
          aqsiSaleHold.initiate({ ...ref, saleId, intent: newCommandIntent(), expectedVersion }, { track: false }),
        ).unwrap()
        if (epochOf(ref.resourceId) !== epoch) return
        showSale(dispatch, ref, result.sale)
        dispatch(lineSelected({ ...ref, key: null }))
        dispatch(
          feedbackSet({
            key,
            feedback: note('sale.hold', ref.registerKey, 'done', 'Чек отложен. Откроете его из «Отложенные», когда покупатель вернётся.'),
          }),
        )
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        dispatch(feedbackSet({ key, feedback: feedbackOfError('sale.hold', ref.registerKey, error) }))
      }
    }),

    // UI-RMK: «Открыть» отложенный — текущий чек с позициями сначала откладывается (чеки меняются местами).
    defineListener<S, ReturnType<typeof heldCartRestoreRequested>>(heldCartRestoreRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { heldCartId, current, ...ref } = action.payload
      const key = saleFeedbackKey(ref)
      if (!seatReady(api, ref, 'sale.held-restore')) return
      const epoch = epochOf(ref.resourceId)
      try {
        if (current !== null) {
          const held = await dispatch(
            aqsiSaleHold.initiate(
              { ...ref, saleId: current.saleId, intent: newCommandIntent(), expectedVersion: current.version },
              { track: false },
            ),
          ).unwrap()
          if (epochOf(ref.resourceId) !== epoch) return
          showSale(dispatch, ref, held.sale)
        }
        // Версия МОЕЙ сессии за кассой (как у создания продажи): из свежего ответа касс.
        const own = await dispatch(aqsiMySession.initiate({ resourceId: ref.resourceId }, { subscribe: false, forceRefetch: true }))
        const mine = own.data
        if (mine == null || !sameId(mine.registerKey, ref.registerKey)) {
          dispatch(
            feedbackSet({
              key,
              feedback: note('sale.held-restore', ref.registerKey, 'rejected', 'За этой кассой вы не работаете — начните работу в РМК.'),
            }),
          )
          return
        }
        const sale = await dispatch(
          aqsiHeldCartRestore.initiate(
            { ...ref, heldCartId, intent: newCommandIntent(), expectedVersion: mine.version, paymentKind: null },
            { track: false },
          ),
        ).unwrap()
        if (epochOf(ref.resourceId) !== epoch) return
        showSale(dispatch, ref, sale)
        dispatch(lineSelected({ ...ref, key: null }))
        dispatch(
          feedbackSet({
            key,
            feedback: note(
              'sale.held-restore',
              ref.registerKey,
              'done',
              current !== null ? 'Отложенный чек открыт, а тот, что был в работе, отложен.' : 'Отложенный чек открыт.',
            ),
          }),
        )
      } catch (error) {
        if (epochOf(ref.resourceId) !== epoch) return
        dispatch(feedbackSet({ key, feedback: feedbackOfError('sale.held-restore', ref.registerKey, error) }))
        rereadSeat(dispatch, ref)
      }
    }),

    // UI-RMK: «Удалить» отложенный чек.
    defineListener<S, ReturnType<typeof heldCartDropRequested>>(heldCartDropRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { heldCartId, version, ...ref } = action.payload
      const key = saleFeedbackKey(ref)
      try {
        await dispatch(
          aqsiHeldCartDrop.initiate({ ...ref, heldCartId, intent: newCommandIntent(), expectedVersion: version }, { track: false }),
        ).unwrap()
        dispatch(feedbackSet({ key, feedback: note('sale.held-drop', ref.registerKey, 'done', 'Отложенный чек удалён.') }))
      } catch (error) {
        dispatch(feedbackSet({ key, feedback: feedbackOfError('sale.held-drop', ref.registerKey, error) }))
      }
    }),

    defineListener<S, ReturnType<typeof operationResolveRequested>>(operationResolveRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId, operationId, seat, ...body } = action.payload
      const draft = resolveDraftOf(select(api.getModuleState()).sale, resourceId, operationId)
      // Двойной клик и повтор после неизвестного исхода не шлют второй POST.
      if (draft.phase !== 'editing') return
      const ref = { resourceId, operationId }
      const key = resolveFeedbackKey(resourceId, operationId)
      // Сверка из окна оплаты — итог в окне попытки (RP-PAY-ONE); из отдельного окна сверки — в его итоге.
      const say = (feedback: CommandFeedback): void => {
        if (seat === undefined) dispatch(feedbackSet({ key, feedback }))
        else sayInPaymentWindow(dispatch, { resourceId, registerKey: seat.registerKey }, seat.saleId, feedback)
      }
      dispatch(resolvePhaseChanged({ ...ref, phase: 'sending' }))
      say(note('operation.resolve', operationId, 'sending', null))
      try {
        await dispatch(aqsiOperationResolve.initiate({ ...ref, ...body, intent: newCommandIntent() }, { track: false })).unwrap()
        dispatch(resolvePhaseChanged({ ...ref, phase: 'done' }))
        // Сверка записана: окно оплаты возвращается к этапу, который теперь скажет продажа.
        if (seat !== undefined) dispatch(paymentResolveClosed({ resourceId, registerKey: seat.registerKey }))
        say(note('operation.resolve', operationId, 'done', 'Ручная сверка записана'))
      } catch (error) {
        const result = feedbackOfError('operation.resolve', operationId, error)
        dispatch(resolvePhaseChanged({ ...ref, phase: result.phase === 'unknown' ? 'unknown' : 'editing' }))
        say(result.phase === 'unknown' ? { ...result, note: 'Ответ не получен. Сверка не повторяется — проверьте статус операции.' } : result)
      }
    }),

    defineListener<S, ReturnType<typeof operationResolveCheckRequested>>(
      operationResolveCheckRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const { resourceId, operationId } = action.payload
        const result = await dispatch(
          aqsiSaleOperation.initiate({ resourceId, operationId }, { subscribe: false, forceRefetch: true }),
        )
        const status = result.data
        if (status === undefined) return
        const draft = resolveDraftOf(select(api.getModuleState()).sale, resourceId, operationId)
        if (status.resolution !== null || status.nextAction !== 'ManualReconciliation') {
          dispatch(resolvePhaseChanged({ resourceId, operationId, phase: 'done' }))
        } else if (draft.phase === 'unknown') {
          // Операция всё ещё ждёт сверки: наша не дошла. Новая уйдёт с той же версией — двойную служба отвергнет (409).
          dispatch(resolvePhaseChanged({ resourceId, operationId, phase: 'editing' }))
        }
      },
    ),

    defineListener<S, ReturnType<typeof saleReloadRequested>>(saleReloadRequested.match, (action, api) => {
      const { resourceId, registerKey } = action.payload
      storeDispatch(api.dispatch)(
        saleApi.util.invalidateTags([
          resourceTag(AQSI_TAGS.saleActive, resourceId, registerKey),
          resourceListTag(AQSI_TAGS.registers, resourceId),
          resourceListTag(AQSI_TAGS.goods, resourceId),
        ]),
      )
    }),
  ]
}
